import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import competitionsData from '@/data/competitions.json';
import type { CompetitionEvent } from '@/data/types';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const events = competitionsData as CompetitionEvent[];

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Newest first; events without a date sort last.
const sortedEvents = [...events].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

export default function EventsListPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Events & Competition Results | HUB30 Gym – Jbeil, Lebanon</title>
        <meta
          name="description"
          content="Results and rankings from competitions hosted at HUB30 gym in Jbeil — including our doubles fitness competition. Browse leaderboards by category and search for your name."
        />
        <meta
          name="keywords"
          content="fitness competition Lebanon, fitness competition Jbeil, HUB30 results, gym competition Byblos, doubles competition Lebanon, leaderboard"
        />
        <link rel="canonical" href="https://hub30gym.com/events" />
        <meta property="og:title" content="Events & Competition Results | HUB30 Gym – Jbeil" />
        <meta property="og:description" content="Browse competition leaderboards and results from HUB30 gym in Jbeil, Lebanon." />
        <meta property="og:url" content="https://hub30gym.com/events" />
      </Helmet>

      <Navbar />

      <section className="pt-28 pb-20 lg:pt-32 px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 lg:mb-16">
            <span className="inline-flex items-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
              <span className="w-4 h-px bg-[#14B8A6]" />
              COMPETITIONS
              <span className="w-4 h-px bg-[#14B8A6]" />
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4">Events &amp; Results</h1>
            <p className="text-gray-400 text-base lg:text-lg max-w-2xl mx-auto">
              Leaderboards and rankings from competitions hosted at HUB30. Pick an event to see
              full results and search for your name.
            </p>
          </div>

          {sortedEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <i className="ri-trophy-line text-4xl text-gray-700 mb-4" />
              <p className="text-gray-500">No events published yet. Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {sortedEvents.map((event) => {
                const date = formatDate(event.date);
                const teamCount = event.categories.reduce((n, c) => n + c.results.length, 0);
                return (
                  <Link
                    key={event.id}
                    to={`/events/${event.slug}`}
                    className="group bg-[#1a1a1a] rounded-3xl border border-white/[0.08] hover:border-[#14B8A6]/25 hover:shadow-[0_0_40px_rgba(20,184,166,0.12)] transition-all duration-300 p-6 lg:p-8 flex flex-col"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-block text-xs font-semibold text-[#14B8A6] bg-[#14B8A6]/10 border border-[#14B8A6]/20 px-3 py-1 rounded-full">
                        {event.discipline}
                      </span>
                      <i className="ri-arrow-right-up-line text-gray-600 group-hover:text-[#14B8A6] transition-colors text-xl" />
                    </div>
                    <h2 className="text-xl lg:text-2xl font-bold mb-2">{event.name}</h2>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-gray-500 text-sm mt-auto pt-4">
                      {date && (
                        <span className="inline-flex items-center gap-1.5">
                          <i className="ri-calendar-line" />
                          {date}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-team-line" />
                        {teamCount} teams
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-list-ordered" />
                        {event.categories.length} categories
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
